import { AlertTriangle, CheckCircle2, HelpCircle, Info, Loader, X } from 'lucide-react'
import { createContext, useContext, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { DiagnosticCopy } from './DiagnosticCopy'

export type NotificationTone = 'success' | 'info' | 'danger' | 'warning' | 'progress'

const icons = {
  success: CheckCircle2,
  info: Info,
  danger: AlertTriangle,
  warning: HelpCircle,
  progress: Loader,
}

/** Единый вид краткого сообщения для любого раздела приложения. */
export function Notification({ tone, title, text, hint, meta, actions, onClose }: {
  tone: NotificationTone
  title: string
  text?: string | null
  hint?: string | null
  meta?: string | null
  /** Действия сообщения («Допечатать»): встают рядом с «Скопировать», высота плашки не меняется. */
  actions?: ReactNode
  onClose?: () => void
}) {
  const Icon = icons[tone]
  const report = [title, text, hint, meta].filter(Boolean).join('\n')
  // Крестик есть у любого уведомления (решение владельца 23.10.2026). Если хозяин сообщения своего «закрыть» не дал, уведомление
  // скрывается само — до тех пор, пока его текст не изменится (новое событие показывается снова).
  const [closedReport, setClosedReport] = useState<string | null>(null)
  if (onClose === undefined && closedReport === report) return null
  const close = onClose ?? (() => setClosedReport(report))
  return (
    <div className="krp-notification" data-tone={tone} role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}>
      <Icon size={20} aria-hidden="true" className="krp-notification__icon" />
      {/* Плашка фиксированной высоты: длинный текст прокручивается внутри тела, кнопки справа остаются на месте. */}
      <div className="krp-notification__body" tabIndex={0}>
        <strong>{title}</strong>
        {text && <p>{text}</p>}
        {hint && <p>{hint}</p>}
        {meta && <p>{meta}</p>}
      </div>
      <div className="krp-notification__actions">
        {/* Уведомление — не обязательно ошибка: кнопка копирует текст сообщения целиком. */}
        <DiagnosticCopy text={report} label="Скопировать" />
        {actions}
        <button type="button" className="krp-notification__close" aria-label="Закрыть уведомление" onClick={close}><X size={18} aria-hidden="true" /></button>
      </div>
    </div>
  )
}

/** Область уведомлений располагается поверх всех окон у нижнего края экрана. */
export function NotificationStack({ children }: { children: ReactNode }) {
  return <aside className="krp-notifications" aria-label="Уведомления">{children}</aside>
}

const NotificationTarget = createContext<HTMLElement | null>(null)

/** Одна область уведомлений на всё приложение; источники добавляют сообщения через portal. */
export function NotificationProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<HTMLDivElement | null>(null)
  return (
    <NotificationTarget.Provider value={target}>
      {children}
      <NotificationStack><div className="krp-notifications__items" ref={setTarget} /></NotificationStack>
    </NotificationTarget.Provider>
  )
}

/** Сообщение из любого раздела в общей нижней области. */
export function NotificationPortal(props: Parameters<typeof Notification>[0]) {
  const target = useContext(NotificationTarget)
  return target === null ? null : createPortal(<Notification {...props} />, target)
}
